import { authClient } from '@/lib/api/authClient';
import { tokenStorage } from '@/lib/auth/tokenStorage';

import { authResultSchema } from '../schemas/authSchemas';
import { useAuthStore } from '../store/authStore';

// In-flight refresh promise so concurrent 401s only fire one network call.
// Subsequent callers await the same promise and either all see the new token
// or all fall through to logout.
let inflight: Promise<boolean> | null = null;

export function refreshTokens(): Promise<boolean> {
  if (inflight) {
    return inflight;
  }

  inflight = (async () => {
    try {
      // The refresh token rides the HttpOnly sc_refresh cookie (sent by
      // credentials:'include'); echo the readable sc_csrf cookie back in
      // X-CSRF-Token so the auth double-submit check passes.
      const csrf = tokenStorage.getCsrfToken();
      const raw = await authClient
        .post('auth/refresh', {
          json: {},
          headers: csrf ? { 'X-CSRF-Token': csrf } : undefined,
        })
        .json();

      const result = authResultSchema.parse(raw);
      useAuthStore.getState().setSession(result);
      // The sc_refresh cookie is shared across *.signcare.io, so a refresh after
      // hopping in from Console/Execute can return a token minted for another
      // portal (aud="console"/"execute"). setSession rejects a non-"studio"
      // audience (isAuthenticated stays false); report that as a FAILED refresh
      // so the 401 interceptor doesn't replay a token Studio's backend will
      // reject, and ProtectedRoute runs the OAuth authorize flow (which mints a
      // proper aud="studio" token via the SSO cookie).
      return useAuthStore.getState().isAuthenticated;
    } catch {
      // Refresh failed (token reuse, expired, or network). Treat as full
      // sign-out so the next render redirects to /login via ProtectedRoute.
      useAuthStore.getState().clearSession();
      return false;
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}
